/**
 * Scene to Flow Converter
 * Converts ISceneObj format into Flow graph nodes and edges.
 *
 * Key design: Device triggers are SPLIT into two nodes:
 * - TriggerNode: carries execution mode (once / any change / repeatedly)
 * - ConditionNode: carries device reference, property, operator, value
 * Together they form the complete "when" clause. The Trigger feeds into
 * the Condition, and the Condition has true/false outputs.
 *
 * Non-device triggers (time, weather, sunrise, manual) remain as single
 * self-contained nodes since their execution model is built-in.
 *
 * Leverages the existing Scenes condition/action interpreters from
 * `src/components/Scenes/helpers/` for consistent parsing.
 */

import type { ISceneObj } from '../../../types/SceneType';
import type { FlowNode, FlowEdge, NodeType } from '../types/flow-types';
import { NODE_COLORS } from '../types/flow-types';

// Condition type guards from shared types
import {
	is_device_cond_op_in_r,
	is_device_cond_op_as_key,
	is_device_expr_cond_with_ref,
	is_device_once_trigger,
	is_device_on_any_change_trigger,
	is_repeat_device_trigger,
	is_scene_as_trigger,
	is_sunrise_sunset_trigger,
	is_energy_exchange_trigger,
	is_weather_one_time_trigger,
	is_weather_on_any_change_trigger,
} from '../../../types/ScenesConditionsTypes';

// Condition interpreters from Scenes helpers
import {
	deviceConditionInterpretator,
	deviceConditionSecondFormat,
	dailyConditionInterpretator,
	oneTimeConditionInterpretator,
	timerConditionInterpretator,
	automationRuns,
	sunriseSunsetInterpretator,
	weatherOnAnyChangeInterpretator,
	weatherOneTimeInterpretator,
	energyExchangeInterpretator,
	mathConditionInterpretator,
} from '../../Scenes/helpers/scene_conditions_to_ui';

// Action interpreters from Scenes helpers
import {
	deviceActionInterpretator,
	notificationActionInterpretator,
	sceneActionInterpretator,
	enableDisableSceneActionInterpretator,
	groupActionInterpretator,
} from '../../Scenes/helpers/scene_actions_to_ui';

import { get } from 'svelte/store';
import { devices } from '../../../stores';
import { conditionToVerboseLabel, generateNodeLabel } from '../PropertyEditors/helpers/LabelGenerator';
import { resolveNotificationPaletteKey } from '../helpers/notificationDisplay';
import { toSceneOperator } from './helpers/device-conditions';
import { migrateFlowCardData } from '../core/card-engine/registry';
import {
	extractSceneIslandIfOrArray,
	isReactiveFlowIslandScene,
	normalizeSceneDoStatements,
	unwrapIslandGate,
} from './flow-island-utils';
import { parseRunScriptFromSceneAction } from 'src/helpers/device_script_action_helpers';
import {
	extractZoneIdFromPresenceCondition,
	getPresenceConditionType,
} from 'src/helpers/presence_scene_tokens';

export { extractSceneIslandIfOrArray };

export interface FlowGraph {
	nodes: FlowNode[];
	edges: FlowEdge[];
}

function migrateCardNode(node: FlowNode): FlowNode {
	const migratedData = migrateFlowCardData(node.type, node.data);
	const migrated = { ...node, data: migratedData as FlowNode['data'] };
	return {
		...migrated,
		data: { ...migrated.data, displayLabel: generateNodeLabel(migrated) },
	};
}

/**
 * A group of nodes produced from a single scene condition.
 * For device triggers this contains [TriggerNode, ConditionNode] + internal edge.
 * For other types it contains a single node.
 * `outputNodeId` is the node that should connect to downstream gates/actions.
 */
interface NodeGroup {
	nodes: FlowNode[];
	edges: FlowEdge[];
	outputNodeId: string;
}

interface BranchGraph {
	nodes: FlowNode[];
	edges: FlowEdge[];
	entryNodeId: string | null;
	tailNodeIds: string[];
	/** Nodes that receive the parent decision's branch edge (true/false) — parallel roots, no action→action edge between them */
	gateTargetIds: string[];
}

/** Flow types that are serialized under do/else (not conditions or wiring nodes) */
const GRAPH_ACTION_LIKE_TYPES = new Set([
	'action',
	'notificationAction',
	'groupAction',
	'sceneAction',
	'alarmAction',
	'scriptAction',
	'setVariable',
]);

/**
 * Removes output→input edges between two nodes that are already siblings on the same
 * decision branch (each has a true/false edge from the same parent). Those edges were
 * incorrectly introduced when flat `do` arrays were deserialized as sequential chains.
 */
function stripRedundantBranchSiblingActionEdges(nodes: FlowNode[], edges: FlowEdge[]): FlowEdge[] {
	const typeById = new Map(nodes.map((n) => [String(n.id), n.type]));
	const isActionLike = (id: string) => GRAPH_ACTION_LIKE_TYPES.has(String(typeById.get(String(id))));

	const branchParent = new Map<string, { parent: string; handle: string }>();
	for (const e of edges) {
		const h = e.sourceHandle;
		if (h !== 'true' && h !== 'false') continue;
		branchParent.set(String(e.target), { parent: String(e.source), handle: h });
	}

	return edges.filter((e) => {
		const sh = e.sourceHandle;
		const th = e.targetHandle;
		// Parasitic links use output→input; React Flow sometimes omits targetHandle (null/undefined)
		const isDefaultTarget = th === 'input' || th === undefined || th === null || String(th) === '';
		if (sh !== 'output' || !isDefaultTarget) return true;

		const src = String(e.source);
		const tgt = String(e.target);
		if (!isActionLike(src) || !isActionLike(tgt)) return true;

		const pSrc = branchParent.get(src);
		const pTgt = branchParent.get(tgt);
		if (!pSrc || !pTgt) return true;
		if (pSrc.parent !== pTgt.parent || pSrc.handle !== pTgt.handle) return true;

		return false;
	});
}

type SavedFlowGraphNode = { id?: string; type?: string; data?: Record<string, unknown> };

/**
 * Map persisted flowGraph node ids onto ids from scene JSON reconstruction.
 * Logic gates lack `_fid` and are re-created with new ids; conditions/actions keep stable `_fid` ids.
 */
function buildFlowGraphNodeIdRemap(
	savedNodes: SavedFlowGraphNode[],
	rebuiltNodes: FlowNode[],
): Map<string, string> {
	const remap = new Map<string, string>();
	const rebuiltIds = new Set(rebuiltNodes.map((n) => String(n.id)));
	const usedGateIds = new Set<string>();

	for (const saved of savedNodes) {
		const sid = String(saved?.id ?? '');
		if (!sid) continue;
		if (rebuiltIds.has(sid)) {
			remap.set(sid, sid);
			continue;
		}

		if (saved.type === 'logicGate') {
			const gateType = String(saved.data?.gateType ?? 'or');
			const conditionCount = Number(saved.data?.conditionCount ?? 2);
			const match = rebuiltNodes.find(
				(n) =>
					n.type === 'logicGate' &&
					!usedGateIds.has(String(n.id)) &&
					String((n.data as { gateType?: string })?.gateType ?? 'or') === gateType &&
					Number((n.data as { conditionCount?: number })?.conditionCount ?? 2) ===
						conditionCount,
			);
			if (match) {
				remap.set(sid, String(match.id));
				usedGateIds.add(String(match.id));
			}
			continue;
		}

		const savedFid = (saved.data?.sceneData as { _fid?: string } | undefined)?._fid;
		if (savedFid) {
			const match = rebuiltNodes.find(
				(n) =>
					String(n.id) === String(savedFid) || (n.data as any)?.sceneData?._fid === savedFid,
			);
			if (match) remap.set(sid, String(match.id));
		}
	}

	return remap;
}

function remapFlowGraphEdgeEndpoints(edge: FlowEdge, nodeIdRemap: Map<string, string>): FlowEdge {
	const source = nodeIdRemap.get(String(edge.source)) ?? String(edge.source);
	const target = nodeIdRemap.get(String(edge.target)) ?? String(edge.target);
	if (source === edge.source && target === edge.target) return edge;
	return { ...edge, source, target, id: edge.id || `e-${source}-${target}` };
}

/** Resolve a flowGraph node entry for a rebuilt node (direct id or logic-gate remap). */
function findSavedFlowGraphNode(
	rebuiltId: string,
	savedNodeById: Map<string, SavedFlowGraphNode>,
	nodeIdRemap: Map<string, string>,
): SavedFlowGraphNode | undefined {
	const direct = savedNodeById.get(rebuiltId);
	if (direct) return direct;
	for (const [savedId, mappedRebuiltId] of nodeIdRemap) {
		if (mappedRebuiltId === rebuiltId) {
			const saved = savedNodeById.get(savedId);
			if (saved) return saved;
		}
	}
	return undefined;
}

function readSavedPosition(saved: SavedFlowGraphNode | undefined): { x: number; y: number } | null {
	const sp = saved?.position;
	if (!sp || typeof sp !== 'object') return null;
	const x = sp.x;
	const y = sp.y;
	if (typeof x === 'number' && typeof y === 'number') return { x, y };
	return null;
}

/** Logic gates lack `_fid`; rename rebuilt gate ids back to persisted flowGraph ids. */
function normalizeLogicGateIdsToFlowGraph(
	nodes: FlowNode[],
	edges: FlowEdge[],
	nodeIdRemap: Map<string, string>,
): { nodes: FlowNode[]; edges: FlowEdge[] } {
	const idChanges = new Map<string, string>();
	for (const [savedId, rebuiltId] of nodeIdRemap) {
		if (savedId === rebuiltId) continue;
		const gate = nodes.find((n) => n.type === 'logicGate' && String(n.id) === rebuiltId);
		if (gate) idChanges.set(rebuiltId, savedId);
	}
	if (idChanges.size === 0) return { nodes, edges };

	const nodesOut = nodes.map((n) =>
		idChanges.has(String(n.id)) ? { ...n, id: idChanges.get(String(n.id))! } : n,
	);
	const edgesOut = edges.map((e) => ({
		...e,
		source: idChanges.get(String(e.source)) ?? e.source,
		target: idChanges.get(String(e.target)) ?? e.target,
		id: e.id,
	}));
	return { nodes: nodesOut, edges: edgesOut };
}

interface IndexedSceneSemantic {
	payload: Record<string, unknown>;
	branch?: 'do' | 'else';
}

/**
 * Index scene-backed node payloads independently of topology reconstruction.
 * This is the v1 fallback for flowGraph entries that intentionally store only layout data.
 */
function indexSceneSemanticsByFid(scene: ISceneObj): Map<string, IndexedSceneSemantic> {
	const indexed = new Map<string, IndexedSceneSemantic>();

	function walk(value: unknown, branch?: 'do' | 'else'): void {
		if (Array.isArray(value)) {
			value.forEach((entry) => walk(entry, branch));
			return;
		}
		if (!value || typeof value !== 'object') return;
		const record = value as Record<string, unknown>;
		const fid = String(record._fid || '').trim();
		if (fid) indexed.set(fid, { payload: record, branch });

		for (const [key, child] of Object.entries(record)) {
			if (key === 'if') walk(child);
			else if (key === 'do') walk(child, 'do');
			else if (key === 'else') walk(child, 'else');
			else if (key !== '_fid') walk(child, branch);
		}
	}

	walk(scene.if);
	walk(scene.do, 'do');
	walk(scene.else, 'else');
	return indexed;
}

function hydrateNodeFromIndexedSemantics(
	node: FlowNode,
	indexed: Map<string, IndexedSceneSemantic>,
): FlowNode {
	if (node.data?.sceneData) return node;
	const semantic = indexed.get(String(node.id));
	if (!semantic) return node;
	const payload = semantic.payload as any;
	const data = {
		...node.data,
		sceneData: semantic.payload,
		runtimeOrphan: false,
		...(semantic.branch ? { branch: semantic.branch } : {}),
		...(node.type === 'notificationAction'
			? {
					notificationType: payload.notify || node.data.notificationType,
					notificationMessage: payload.msg ?? node.data.notificationMessage,
					msg: payload.msg ?? node.data.msg,
				}
			: {}),
	};
	const hydrated = { ...node, data };
	return {
		...hydrated,
		data: {
			...data,
			displayLabel: generateNodeLabel(hydrated),
		},
	};
}

/**
 * Converts a Shelly scene object into a flow graph structure.
 * Handles all known condition/trigger/action JSON formats.
 */
export function sceneToGraph(scene: ISceneObj): FlowGraph {
	const savedGraph = (scene as any)?._meta?.flowGraph;
	if (savedGraph?.nodes && savedGraph?.edges) {
		const savedNodes = normalizeSavedCollection(savedGraph.nodes);
		const savedEdges = normalizeSavedCollection(savedGraph.edges);
		const semanticsByFid = indexSceneSemanticsByFid(scene);
		// flowGraph is always version 1: semantics from if/do/else; merge layout/orphans from `_meta.flowGraph`.
		const rebuilt = buildGraphFromSceneContent(scene);
		const savedNodeById = new Map(savedNodes.map((n) => [String(n.id), n]));
		const savedEdgeById = new Map(savedEdges.map((e) => [String(e?.id ?? ''), e]));
		const rebuiltNodeIds = new Set(rebuilt.nodes.map((n) => String(n.id)));
		const nodeIdRemap = buildFlowGraphNodeIdRemap(savedNodes, rebuilt.nodes);

		const nodes = rebuilt.nodes.map((node) => {
			const saved = findSavedFlowGraphNode(String(node.id), savedNodeById, nodeIdRemap);
			const savedPos = readSavedPosition(saved);
			const withPosition = {
				...node,
				position: savedPos ?? node.position,
			};
			let data = { ...withPosition.data };
			const sd = saved?.data;
			if (sd?.isLabelManuallyOverridden) {
				data = {
					...data,
					isLabelManuallyOverridden: true,
					...(sd.displayLabel != null ? { displayLabel: sd.displayLabel } : {}),
					...(sd.label != null ? { label: sd.label } : {}),
				};
			}
			if (typeof sd?.paletteKey === 'string' && sd.paletteKey.length > 0) {
				data = { ...data, paletteKey: sd.paletteKey };
			}
			if (
				(node.type === 'switchNode' || node.type === 'variableSwitch') &&
				sd &&
				typeof sd === 'object'
			) {
				const switchUiKeys = [
					'deviceId',
					'deviceName',
					'channel',
					'property',
					'propertyDisplayValue',
					'add_info_ind',
					'input_index',
					'devMode',
				] as const;
				for (const key of switchUiKeys) {
					if (sd[key] != null) data = { ...data, [key]: sd[key] };
				}

				// Runtime semantics remain authoritative for operators/values. Persisted
				// graph metadata supplies stable case ids and user-edited labels.
				if (Array.isArray(data.cases) && Array.isArray(sd.cases)) {
					data = {
						...data,
						cases: data.cases.map((flowCase: Record<string, unknown>, index: number) => {
							const savedCase = sd.cases?.[index] as Record<string, unknown> | undefined;
							if (!savedCase) return flowCase;
							return {
								...flowCase,
								...(savedCase.id != null ? { id: savedCase.id } : {}),
								...(savedCase.label != null ? { label: savedCase.label } : {}),
							};
						}),
					};
				}
			}
			if (
				(node.type === 'hysteresisCondition' ||
					node.type === 'actionSequence' ||
					node.type === 'presenceCondition' ||
					node.type === 'virtualComponentEvent' ||
					node.type === 'virtualComponentCondition') &&
				sd &&
				typeof sd === 'object'
			) {
				data = { ...data, ...sd, runtimeOrphan: false };
			}
			if (node.type === 'scriptAction' && sd && typeof sd === 'object') {
				const {
					deviceRealId,
					deviceId,
					deviceName,
					roomId,
					roomName,
					channel,
					scriptId,
					scriptName,
					command,
					delay,
					label,
					displayLabel,
					isLabelManuallyOverridden,
				} = sd as Record<string, unknown>;
				data = {
					...data,
					...(deviceRealId != null ? { deviceRealId } : {}),
					...(deviceId != null ? { deviceId } : {}),
					...(deviceName != null ? { deviceName } : {}),
					...(roomId != null ? { roomId } : {}),
					...(roomName != null ? { roomName } : {}),
					...(channel != null ? { channel } : {}),
					...(scriptId != null ? { scriptId } : {}),
					...(scriptName != null ? { scriptName } : {}),
					...(command != null ? { command } : {}),
					...(delay != null ? { delay } : {}),
					...(label != null ? { label } : {}),
					...(displayLabel != null ? { displayLabel } : {}),
					...(isLabelManuallyOverridden != null ? { isLabelManuallyOverridden } : {}),
				};
			}
			const interim = { ...withPosition, data };
			return {
				...interim,
				data: {
					...interim.data,
					displayLabel: generateNodeLabel(interim),
				},
			};
		});

		// Orphans / nodes missing from scene reconstruction
		for (const saved of savedNodes) {
			const id = String(saved?.id ?? '');
			if (!id || rebuiltNodeIds.has(id)) continue;

			/* Legacy `wait` blocks had no `_fid`; reconstruction adds a timer from scene `do`, while
			 * flowGraph still lists the real timer as an orphan — skip duplicate timer + duplicate edges. */
			if (saved.type === 'timer' && saved.data?.delay) {
				const d = saved.data.delay;
				const w = saved.data._wid;
				const isDup = rebuilt.nodes.some(
					(n) =>
						n.type === 'timer' &&
						(n.data as any)?.delay?.value === d.value &&
						(n.data as any)?.delay?.units === d.units &&
						(w === undefined || (n.data as any)?._wid === w),
				);
				if (isDup) continue;
			}

			if (saved.type === 'logicGate') {
				const gateType = String(saved.data?.gateType ?? 'or');
				const conditionCount = Number(saved.data?.conditionCount ?? 2);
				const isDupGate = rebuilt.nodes.some(
					(n) =>
						n.type === 'logicGate' &&
						String((n.data as any)?.gateType ?? 'or') === gateType &&
						Number((n.data as any)?.conditionCount ?? 2) === conditionCount,
				);
				if (isDupGate) continue;
			}

			const restoredData = {
				...(saved.data || { label: saved.data?.label ?? 'Node' }),
				runtimeOrphan: true,
			};
			const restoredNode: FlowNode = {
				id: saved.id,
				type: (saved.type || 'condition') as NodeType,
				position: saved.position || { x: 0, y: 0 },
				data: restoredData,
				validated: saved.validated !== false,
				errors: Array.isArray(saved.errors) ? saved.errors : [],
			};
			nodes.push({
				...restoredNode,
				data: {
					...restoredNode.data,
					displayLabel: generateNodeLabel(restoredNode),
				},
			});
		}

		const seenNodeIds = new Set<string>();
		const dedupedNodes = nodes.filter((n) => {
			if (seenNodeIds.has(String(n.id))) return false;
			seenNodeIds.add(String(n.id));
			return true;
		});

		const normalizedGraph = normalizeLogicGateIdsToFlowGraph(
			dedupedNodes,
			rebuilt.edges,
			nodeIdRemap,
		);
		const finalNodes = normalizedGraph.nodes.map((node) =>
			hydrateNodeFromIndexedSemantics(node, semanticsByFid),
		);
		const rebuiltEdgesNormalized = normalizedGraph.edges;

		// Rebuild remap after gate ids may have been renamed to persisted flowGraph ids.
		const edgeNodeIdRemap = buildFlowGraphNodeIdRemap(savedNodes, finalNodes);

		const allNodeIds = new Set(finalNodes.map((n) => String(n.id)));

		function edgeTopologyKey(e: {
			source: string;
			target: string;
			sourceHandle?: string | null;
			targetHandle?: string | null;
		}): string {
			return `${e.source}\0${e.sourceHandle ?? ''}\0${e.target}\0${e.targetHandle ?? ''}`;
		}

		const savedEdgesFiltered = savedEdges
			.filter((e) => e && String(e.source) !== String(e.target))
			.map((e) => remapFlowGraphEdgeEndpoints(e as FlowEdge, edgeNodeIdRemap))
			.filter((e) => allNodeIds.has(String(e.source)) && allNodeIds.has(String(e.target)));

		/** Prefer persisted `flowGraph` edges so we do not invent topology (e.g. linear timer chains). */
		if (savedEdgesFiltered.length > 0) {
			const rebuiltByTopo = new Map<string, FlowEdge>();
			for (const e of rebuiltEdgesNormalized) {
				rebuiltByTopo.set(edgeTopologyKey(e), e);
			}

			const seenEdgeIds = new Set<string>();
			const mergedEdges: FlowEdge[] = [];
			for (let i = 0; i < savedEdgesFiltered.length; i++) {
				const s = savedEdgesFiltered[i];
				const sid = String(s.id ?? '').trim();
				const dedupeKey = sid || `${edgeTopologyKey(s)}#${i}`;
				if (seenEdgeIds.has(dedupeKey)) continue;
				seenEdgeIds.add(dedupeKey);

				const r = rebuiltByTopo.get(edgeTopologyKey(s));
				const next: FlowEdge = {
					id: sid || `e-${String(s.source)}-${String(s.target)}-${i}`,
					source: s.source,
					target: s.target,
					sourceHandle: s.sourceHandle,
					targetHandle: s.targetHandle,
					type: (s as any).type || (r as any)?.type || 'default',
					animated: s.animated !== false,
					class: (s as any).class ?? (r as any)?.class,
					style: (s as any).style ?? (r as any)?.style,
				} as FlowEdge;
				const sd = (s as any).data;
				const rd = (r as any)?.data;
				if (sd && typeof sd === 'object' && Object.keys(sd).length > 0) {
					(next as any).data = {
						...(rd && typeof rd === 'object' ? rd : {}),
						...sd,
					};
				} else if (rd && typeof rd === 'object' && Object.keys(rd).length > 0) {
					(next as any).data = rd;
				}
				mergedEdges.push(next);
			}

			return {
				nodes: finalNodes,
				edges: stripRedundantBranchSiblingActionEdges(finalNodes, mergedEdges),
			};
		}

		const rebuiltEdgeIds = new Set(rebuiltEdgesNormalized.map((e) => String(e.id)));
		const mergedEdges = rebuiltEdgesNormalized.map((e) => {
			const s = savedEdgeById.get(String(e.id));
			let next: FlowEdge = { ...e };
			if (s?.type) next = { ...next, type: s.type };
			if (s?.data && typeof s.data === 'object' && Object.keys(s.data).length > 0) {
				next = {
					...next,
					data: {
						...((next as any).data && typeof (next as any).data === 'object'
							? (next as any).data
							: {}),
						...s.data,
					},
				};
			}
			return next;
		});
		for (const savedEdge of savedEdges) {
			if (rebuiltEdgeIds.has(String(savedEdge.id))) continue;
			const remapped = remapFlowGraphEdgeEndpoints(savedEdge as FlowEdge, edgeNodeIdRemap);
			if (String(remapped.source) === String(remapped.target)) continue;
			if (allNodeIds.has(String(remapped.source)) && allNodeIds.has(String(remapped.target))) {
				mergedEdges.push({
					id: remapped.id,
					source: remapped.source,
					target: remapped.target,
					sourceHandle: remapped.sourceHandle,
					targetHandle: remapped.targetHandle,
					type: savedEdge.type || 'default',
					animated: savedEdge.animated !== false,
					class: savedEdge.class,
					style: savedEdge.style,
					...(savedEdge.data && typeof savedEdge.data === 'object'
						? { data: savedEdge.data }
						: {}),
				} as FlowEdge);
			}
		}

		const seenEdgeIds = new Set<string>();
		const dedupedEdges = mergedEdges.filter((e) => {
			if (String(e.source) === String(e.target)) return false;
			if (seenEdgeIds.has(String(e.id))) return false;
			seenEdgeIds.add(String(e.id));
			return true;
		});

		const migratedNodes = finalNodes.map(migrateCardNode);
		return {
			nodes: migratedNodes,
			edges: stripRedundantBranchSiblingActionEdges(finalNodes, dedupedEdges),
		};
	}

	const graph = buildGraphFromSceneContent(scene);
	// Enrich nodes with displayLabel derived from node data
	graph.nodes = graph.nodes.map(migrateCardNode);
	return graph;
}

function buildGraphFromSceneContent(scene: ISceneObj): FlowGraph {
	const nodes: FlowNode[] = [];
	const edges: FlowEdge[] = [];
	let nodeIdCounter = 1;
	const getNodeId = () => `node-${nodeIdCounter++}`;

	// Read the devices store once for the entire conversion instead of once per condition node.
	let devicesMap: Record<string, { name?: string }> = {};
	try {
		devicesMap = (get(devices) as Record<string, { name?: string }>) ?? {};
	} catch {
		devicesMap = {};
	}

	// Switch-node flows carry a tagged nested if/do/else chain. Reconstruct the
	// semantic switch even without flowGraph; saved metadata may still enrich layout.
	const doStatements = normalizeBranchStatements(scene.do || []);
	if (doStatements.length === 1 && (doStatements[0] as any)?._switchNodeId) {
		return buildSwitchStatementGraph(doStatements[0], getNodeId, devicesMap);
	}
	if (doStatements.length === 1 && (doStatements[0] as any)?._hysteresisNodeId) {
		return buildHysteresisStatementGraph(doStatements[0], getNodeId, devicesMap);
	}

	if (isReactiveFlowIslandScene(scene)) {
		for (const stmt of normalizeSceneDoStatements(scene.do)) {
			mergeBranchGraphInto(
				nodes,
				edges,
				buildIfStatementGraph(unwrapIslandGate(stmt), getNodeId, devicesMap),
			);
		}
		return { nodes, edges };
	}

	const islandOrLimbs = extractSceneIslandIfOrArray(scene.if);
	const hasIndailyint =
		Array.isArray((scene.if as any)?.and) &&
		(scene.if as any).and.some(
			(x: any) => x && Array.isArray(x.indailyint) && x.indailyint.length > 0,
		);
	const looksLikeFlowIslandBlk =
		!hasIndailyint &&
		islandOrLimbs &&
		doStatements.length === islandOrLimbs.length &&
		doStatements.length > 0 &&
		doStatements.every((s) => s && typeof s === 'object' && s.if != null && s.do !== undefined) &&
		!(scene.else as any[])?.length;

	if (looksLikeFlowIslandBlk) {
		for (const stmt of normalizeSceneDoStatements(scene.do)) {
			const g = buildIfStatementGraph(unwrapIslandGate(stmt), getNodeId, devicesMap);
			nodes.push(...g.nodes);
			edges.push(...g.edges);
		}
		return { nodes, edges };
	}

	// Step 1: Process conditions (if.or / if.and structure)
	const { conditionNodes, conditionEdges, gateNodeId, lastOutputId } = processConditions(
		scene,
		getNodeId,
		devicesMap,
	);
	nodes.push(...conditionNodes);
	edges.push(...conditionEdges);

	// Step 2: Process "do" actions
	const doBranch = buildBranchGraph(scene.do || [], getNodeId, 'do', devicesMap);
	nodes.push(...doBranch.nodes);
	edges.push(...doBranch.edges);

	// Step 3: Process "else" actions
	const elseBranch = buildBranchGraph(scene.else || [], getNodeId, 'else', devicesMap);
	nodes.push(...elseBranch.nodes);
	edges.push(...elseBranch.edges);

	// Step 4: Connect the last condition output → actions
	const connectFromId = gateNodeId || lastOutputId;

	if (connectFromId) {
		for (const targetId of doBranch.gateTargetIds) {
			edges.push({
				id: `e-${connectFromId}-${targetId}-true`,
				source: connectFromId,
				target: targetId,
				sourceHandle: 'true',
				targetHandle: 'input',
				type: 'default',
				animated: true,
			});
		}
		for (const targetId of elseBranch.gateTargetIds) {
			edges.push({
				id: `e-${connectFromId}-${targetId}-false`,
				source: connectFromId,
				target: targetId,
				sourceHandle: 'false',
				targetHandle: 'input',
				type: 'default',
				animated: true,
			});
		}
	}

	return { nodes, edges };
}

type ParsedSwitchCase = {
	ref: string;
	operator: string;
	value: unknown;
	value2?: unknown;
};

function readSwitchRef(value: any): string {
	return String(value?.rr ?? value?.r ?? '');
}

function parseSwitchCaseExpression(expression: any): ParsedSwitchCase | null {
	if (!expression || typeof expression !== 'object') return null;
	if (Array.isArray(expression.and) && expression.and.length === 2) {
		const [lower, upper] = expression.and;
		const lowerOp = ['ge', 'gt'].find((key) => Array.isArray(lower?.[key]));
		const upperOp = ['le', 'lt'].find((key) => Array.isArray(upper?.[key]));
		if (!lowerOp || !upperOp) return null;
		const lowerArgs = lower[lowerOp];
		const upperArgs = upper[upperOp];
		const ref = readSwitchRef(lowerArgs?.[0]);
		if (!ref || ref !== readSwitchRef(upperArgs?.[0])) return null;
		return {
			ref,
			operator: lowerOp === 'ge' && upperOp === 'le' ? 'between_inc' : 'between_exc',
			value: lowerArgs[1],
			value2: upperArgs[1],
		};
	}

	const expressionOp = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].find((key) =>
		Array.isArray(expression[key]),
	);
	if (!expressionOp) return null;
	const args = expression[expressionOp];
	const ref = readSwitchRef(args?.[0]);
	if (!ref) return null;
	const operatorMap: Record<string, string> = {
		eq: 'eq',
		neq: 'ne',
		gt: 'gt',
		gte: 'ge',
		lt: 'lt',
		lte: 'le',
	};
	return { ref, operator: operatorMap[expressionOp], value: args[1] };
}

/** Rebuild a Switch node and case/default edges directly from its tagged scene chain. */
function buildSwitchStatementGraph(
	statement: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): FlowGraph {
	const switchId = String(statement._switchNodeId || getNodeId());
	const nodes: FlowNode[] = [];
	const edges: FlowEdge[] = [];
	const cases: Array<{
		id: string;
		label: string;
		operator: string;
		value: string;
		value2?: string;
	}> = [];
	let sourceRef = '';
	let cursor = statement;
	let caseIndex = 0;

	while (cursor && typeof cursor === 'object' && cursor.if && cursor.do !== undefined) {
		const parsed = parseSwitchCaseExpression(cursor.if);
		if (!parsed) break;
		if (!sourceRef) sourceRef = parsed.ref;
		const caseId = `c${caseIndex}`;
		cases.push({
			id: caseId,
			label: `Case ${caseIndex + 1}`,
			operator: parsed.operator,
			value: String(parsed.value ?? ''),
			...(parsed.value2 !== undefined ? { value2: String(parsed.value2) } : {}),
		});
		const branch = buildBranchGraph(cursor.do, getNodeId, 'do', devicesMap);
		nodes.push(...branch.nodes);
		edges.push(...branch.edges);
		const targets = branch.gateTargetIds.length
			? branch.gateTargetIds
			: branch.entryNodeId
				? [branch.entryNodeId]
				: [];
		for (const target of targets) {
			edges.push({
				id: `e-${switchId}-case-${caseIndex}-${target}`,
				source: switchId,
				target,
				sourceHandle: `case-${caseIndex}`,
				targetHandle: 'input',
				type: 'default',
				animated: true,
			});
		}
		cursor = cursor.else;
		caseIndex += 1;
	}

	const defaultBranch = buildBranchGraph(cursor, getNodeId, 'else', devicesMap);
	nodes.push(...defaultBranch.nodes);
	edges.push(...defaultBranch.edges);
	const defaultTargets = defaultBranch.gateTargetIds.length
		? defaultBranch.gateTargetIds
		: defaultBranch.entryNodeId
			? [defaultBranch.entryNodeId]
			: [];
	for (const target of defaultTargets) {
		edges.push({
			id: `e-${switchId}-default-${target}`,
			source: switchId,
			target,
			sourceHandle: 'default',
			targetHandle: 'input',
			type: 'default',
			animated: true,
		});
	}

	const parts = sourceRef.split(':');
	const deviceId = parts[0] === 'dev' ? parts[1] || '' : '';
	const property = parts.length > 2 ? parts.slice(2).join(':') : sourceRef;
	const isVariable = sourceRef.startsWith('var:');
	const switchNode = createFlowNode(switchId, isVariable ? 'variableSwitch' : 'switchNode', {
		label: isVariable ? 'Variable Switch' : 'Switch',
		icon: 'fa-shuffle',
		sourceRef,
		sourceLabel: property,
		...(isVariable ? { variableName: sourceRef.slice(4) } : {}),
		deviceId,
		deviceName: getDeviceNameFromMap(deviceId, devicesMap),
		property,
		cases,
		isValid: !!sourceRef && cases.length > 0,
		isConfigured: !!sourceRef && cases.length > 0,
	});
	return { nodes: [switchNode, ...nodes], edges };
}

function buildHysteresisStatementGraph(
	statement: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): FlowGraph {
	const id = String(statement._hysteresisNodeId || getNodeId());
	const onExpr = parseSwitchCaseExpression(statement.if);
	const offBlock = statement.else && typeof statement.else === 'object' ? statement.else : {};
	const offExpr = parseSwitchCaseExpression(offBlock.if);
	const ref = onExpr?.ref || offExpr?.ref || '';
	const parts = ref.split(':');
	const deviceId = parts[0] === 'dev' ? parts[1] || '' : '';
	const property = parts.length > 2 ? parts.slice(2).join(':') : '';
	const node = createFlowNode(id, 'hysteresisCondition', {
		label: 'Hysteresis Condition',
		icon: 'fa-wave-square',
		deviceId,
		deviceName: getDeviceNameFromMap(deviceId, devicesMap),
		property,
		sourceRef: ref,
		upperThreshold: Number(onExpr?.value),
		lowerThreshold: Number(offExpr?.value),
		isValid: Boolean(ref && onExpr && offExpr),
		isConfigured: Boolean(ref && onExpr && offExpr),
	});
	const onBranch = buildBranchGraph(statement.do, getNodeId, 'do', devicesMap);
	const offBranch = buildBranchGraph(offBlock.do, getNodeId, 'else', devicesMap);
	const nodes = [node, ...onBranch.nodes, ...offBranch.nodes];
	const edges = [...onBranch.edges, ...offBranch.edges];
	for (const target of onBranch.gateTargetIds)
		edges.push({
			id: `e-${id}-${target}-true`,
			source: id,
			target,
			sourceHandle: 'true',
			targetHandle: 'input',
			type: 'default',
			animated: true,
		});
	for (const target of offBranch.gateTargetIds)
		edges.push({
			id: `e-${id}-${target}-false`,
			source: id,
			target,
			sourceHandle: 'false',
			targetHandle: 'input',
			type: 'default',
			animated: true,
		});
	return { nodes, edges };
}

/**
 * Walk the nested if/do/else chain produced by a switch node and collect all
 * leaf action nodes.  Each leaf carries a `_fid` so parseActionToNode gives it
 * a stable ID that matches savedGraph and the saved edges.
 */
function collectSwitchLeafActions(
	block: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): FlowNode[] {
	const result: FlowNode[] = [];
	if (!block) return result;

	if (block.if) {
		// if/do/else structure — recurse into both branches
		const doVal = block.do;
		if (doVal !== undefined && doVal !== null) {
			result.push(...collectSwitchLeafActions(doVal, getNodeId, devicesMap));
		}
		if (block.else !== undefined && block.else !== null) {
			result.push(...collectSwitchLeafActions(block.else, getNodeId, devicesMap));
		}
	} else if (Array.isArray(block)) {
		for (const item of block) {
			result.push(...collectSwitchLeafActions(item, getNodeId, devicesMap));
		}
	} else if (block.blk && Array.isArray(block.blk)) {
		for (const item of block.blk) {
			result.push(...collectSwitchLeafActions(item, getNodeId, devicesMap));
		}
	} else if (typeof block === 'object' && Object.keys(block).length > 0) {
		// Leaf action object
		const node = parseActionToNode(block, getNodeId, 'do');
		if (node) result.push(node);
	}

	return result;
}

function normalizeSavedCollection(input: any[] | Record<string, any>): any[] {
	if (Array.isArray(input)) return input;
	if (input && typeof input === 'object') {
		return Object.values(input).filter((item) => item && typeof item === 'object');
	}
	return [];
}

function getStableNodeId(payload: any, getNodeId: () => string): string {
	const fid = payload?._fid || payload?.id;
	return typeof fid === 'string' && fid.trim() !== '' ? fid : getNodeId();
}

// ---- Condition processing ----

function logicGateInputHandle(index: number): string {
	return `input-${index}`;
}

function findPresenceRef(value: unknown): string {
	if (Array.isArray(value)) {
		for (const entry of value) {
			const found = findPresenceRef(entry);
			if (found) return found;
		}
		return '';
	}
	if (!value || typeof value !== 'object') return '';
	const record = value as Record<string, unknown>;
	for (const key of ['rr', 'r']) {
		if (
			typeof record[key] === 'string' &&
			/:prsn_(?:st|nobj_d|nobj)4\d{3}$/.test(record[key] as string)
		)
			return record[key] as string;
	}
	for (const child of Object.values(record)) {
		const found = findPresenceRef(child);
		if (found) return found;
	}
	return '';
}

function isPresenceSceneCondition(cond: unknown): boolean {
	return (cond as any)?._flow_card === 'presenceCondition' || Boolean(findPresenceRef(cond));
}

function parseVirtualComponentRef(ref: unknown): {
	backendId: string;
	type: 'button' | 'number' | 'boolean' | 'enum' | 'text';
	index: number;
} | null {
	const match = String(ref || '').match(/^dev:([^:]+):(button|number|boolean|enum|text)(\d+)$/);
	if (!match) return null;
	return {
		backendId: match[1],
		type: match[2] as 'button' | 'number' | 'boolean' | 'enum' | 'text',
		index: Number(match[3]) - 1,
	};
}

function virtualConditionRuntime(cond: any): {
	component: Record<string, any>;
	operator: string;
	value: unknown;
} | null {
	const expressionOperator = ['eq', 'ne', 'neq', 'lt', 'gt', 'le', 'ge'].find((operator) =>
		Array.isArray(cond?.[operator]),
	);
	const ref = cond?.r || cond?.rr || (expressionOperator ? cond[expressionOperator]?.[0]?.rr : '');
	const parsed = parseVirtualComponentRef(ref);
	if (!parsed) return null;
	const parentDeviceId = String(
		cond?._flow_virtual_component?.parentDeviceId || cond?._gui_device_id || parsed.backendId,
	).split('_')[0];
	const componentKey = `${parsed.type}:${parsed.index}`;
	const component = {
		...(cond?._flow_virtual_component || {}),
		ref: `${parentDeviceId}|${componentKey}`,
		parentDeviceId,
		componentKey,
		type: parsed.type,
		index: parsed.index,
		name: cond?._flow_virtual_component?.name || `${parsed.type} ${parsed.index}`,
	};
	return {
		component,
		operator: String(cond?.cmpop || cond?.thsop || expressionOperator || 'eq').replace('neq', 'ne'),
		value:
			cond?.cmpval ??
			cond?.thsval ??
			(expressionOperator ? cond[expressionOperator]?.[1] : undefined),
	};
}

function convertGroupToPresence(group: NodeGroup, cond: any): NodeGroup {
	const ref = findPresenceRef(cond);
	const conditionName = ref.split(':').pop() || '';
	const zoneId = extractZoneIdFromPresenceCondition(conditionName) || '';
	const conditionType = getPresenceConditionType(conditionName);
	const operator = String(
		cond.thsop || cond.cmpop || ['eq', 'lt', 'gt'].find((key) => Array.isArray(cond[key])) || 'eq',
	);
	const value =
		cond.thsval ?? cond.cmpval ?? (Array.isArray(cond[operator]) ? cond[operator][1] : undefined);
	let presenceEvent = 'presence';
	if (conditionType === 'state') {
		presenceEvent =
			String(value) === 'false' || value === 0 || value === '0' ? 'absence' : 'presence';
	} else if (conditionType === 'delta') {
		presenceEvent = operator === 'lt' ? 'leave' : 'enter';
	} else if (conditionType === 'object_count') {
		presenceEvent = 'object_count';
	}
	return {
		...group,
		nodes: group.nodes.map((node) => ({
			...node,
			type: 'presenceCondition',
			data: {
				...node.data,
				label: 'Presence',
				icon: 'fa-person-rays',
				color: '#06b6d4',
				zoneId,
				presenceEvent,
				objectOperator: operator,
				objectCount: Number(value ?? 1),
				executionMode: node.data.executionMode || 'any',
				cardVersion: String(cond._flow_card_version || '1.0.0'),
				isValid: true,
				isConfigured: true,
			},
		})),
	};
}

function processConditions(
	scene: ISceneObj,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): {
	conditionNodes: FlowNode[];
	conditionEdges: FlowEdge[];
	gateNodeId: string | null;
	lastOutputId: string | null;
} {
	const conditionNodes: FlowNode[] = [];
	const conditionEdges: FlowEdge[] = [];
	let gateNodeId: string | null = null;
	let lastOutputId: string | null = null;

	// Simple OR structure: if.or = [cond1, cond2, ...]
	if (scene.if?.or && Array.isArray(scene.if.or) && scene.if.or.length > 0) {
		const orConditions = scene.if.or;

		if (orConditions.length === 1) {
			// Single condition - no gate needed
			const group = parseConditionToNodeGroup(orConditions[0], getNodeId, devicesMap);
			conditionNodes.push(...group.nodes);
			conditionEdges.push(...group.edges);
			lastOutputId = group.outputNodeId;
		} else {
			// Multiple conditions in OR - create OR logic gate
			const orGateId = getNodeId();
			gateNodeId = orGateId;

			conditionNodes.push(
				createFlowNode(orGateId, 'logicGate', {
					label: 'Any condition matches',
					gateType: 'or',
					conditionCount: orConditions.length,
					color: NODE_COLORS.logicGate,
				}),
			);

			orConditions.forEach((cond, orInputIndex) => {
				const group = parseConditionToNodeGroup(cond, getNodeId, devicesMap);
				conditionNodes.push(...group.nodes);
				conditionEdges.push(...group.edges);
				// Connect group output → OR gate
				conditionEdges.push({
					id: `e-${group.outputNodeId}-${orGateId}`,
					source: group.outputNodeId,
					target: orGateId,
					sourceHandle: 'output',
					targetHandle: logicGateInputHandle(orInputIndex),
					type: 'default',
					animated: true,
				});
			});
		}
	}

	// AND structure: if.and = [{or: [...]}, {or: [...]}, ...]
	else if (scene.if?.and && Array.isArray(scene.if.and)) {
		const conditionGroups = (scene.if.and as any[]).filter(
			(group: any) => group.or && Array.isArray(group.or),
		);
		const scheduleGroup = (scene.if.and as any[]).find(
			(group: any) => group.indailyint && Array.isArray(group.indailyint),
		);

		if (conditionGroups.length > 0 || scheduleGroup) {
			// If only schedule exists, no AND gate needed.
			if (conditionGroups.length === 0 && scheduleGroup) {
				const scheduleId = getStableNodeId(scheduleGroup, getNodeId);
				conditionNodes.push(
					createFlowNode(scheduleId, 'scheduleCondition', {
						label: 'Schedule',
						icon: 'fa-calendar',
						activeTimeIntervals: scheduleGroup.indailyint,
						sceneData: { indailyint: scheduleGroup.indailyint },
						isValid: true,
						isConfigured: true,
					}),
				);
				lastOutputId = scheduleId;
				return { conditionNodes, conditionEdges, gateNodeId, lastOutputId };
			}

			const andGateId = getNodeId();
			gateNodeId = andGateId;
			const andInputCount = conditionGroups.length + (scheduleGroup ? 1 : 0);

			conditionNodes.push(
				createFlowNode(andGateId, 'logicGate', {
					label: 'All conditions must match',
					gateType: 'and',
					conditionCount: andInputCount,
					color: NODE_COLORS.logicGate,
				}),
			);

			conditionGroups.forEach((group: any, andInputIndex: number) => {
				if (group.or.length === 1) {
					const nodeGroup = parseConditionToNodeGroup(group.or[0], getNodeId, devicesMap);
					conditionNodes.push(...nodeGroup.nodes);
					conditionEdges.push(...nodeGroup.edges);
					conditionEdges.push({
						id: `e-${nodeGroup.outputNodeId}-${andGateId}`,
						source: nodeGroup.outputNodeId,
						target: andGateId,
						sourceHandle: 'output',
						targetHandle: logicGateInputHandle(andInputIndex),
						type: 'default',
						animated: true,
					});
				} else if (group.or.length > 1) {
					// Nested OR gate inside AND
					const nestedOrId = getNodeId();
					conditionNodes.push(
						createFlowNode(nestedOrId, 'logicGate', {
							label: 'Any condition matches',
							gateType: 'or',
							conditionCount: group.or.length,
							color: NODE_COLORS.logicGate,
						}),
					);

					conditionEdges.push({
						id: `e-${nestedOrId}-${andGateId}`,
						source: nestedOrId,
						target: andGateId,
						sourceHandle: 'output',
						targetHandle: logicGateInputHandle(andInputIndex),
						type: 'default',
						animated: true,
					});

					group.or.forEach((cond: any, nestedOrInputIndex: number) => {
						const nodeGroup = parseConditionToNodeGroup(cond, getNodeId, devicesMap);
						conditionNodes.push(...nodeGroup.nodes);
						conditionEdges.push(...nodeGroup.edges);
						conditionEdges.push({
							id: `e-${nodeGroup.outputNodeId}-${nestedOrId}`,
							source: nodeGroup.outputNodeId,
							target: nestedOrId,
							sourceHandle: 'output',
							targetHandle: logicGateInputHandle(nestedOrInputIndex),
							type: 'default',
							animated: true,
						});
					});
				}
			});

			if (scheduleGroup) {
				const scheduleId = getStableNodeId(scheduleGroup, getNodeId);
				conditionNodes.push(
					createFlowNode(scheduleId, 'scheduleCondition', {
						label: 'Schedule',
						icon: 'fa-calendar',
						activeTimeIntervals: scheduleGroup.indailyint,
						sceneData: { indailyint: scheduleGroup.indailyint },
						isValid: true,
						isConfigured: true,
					}),
				);
				conditionEdges.push({
					id: `e-${scheduleId}-${andGateId}`,
					source: scheduleId,
					target: andGateId,
					sourceHandle: 'output',
					targetHandle: logicGateInputHandle(conditionGroups.length),
					type: 'default',
					animated: true,
				});
			}
		}
	}

	return { conditionNodes, conditionEdges, gateNodeId, lastOutputId };
}

/**
 * Parse a single scene condition JSON object into a NodeGroup.
 * For device triggers, this creates [TriggerNode] → [ConditionNode].
 * For other types, this creates a single node.
 */
function parseConditionToNodeGroup(
	cond: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): NodeGroup {
	if (!cond) {
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [createFlowNode(id, 'condition', { label: 'Empty', isValid: false })],
			edges: [],
			outputNodeId: id,
		};
	}

	const purpose = cond._gui_purpose || cond._gui_type;
	const asPresence = (group: NodeGroup): NodeGroup =>
		isPresenceSceneCondition(cond) ? convertGroupToPresence(group, cond) : group;
	const virtual = virtualConditionRuntime(cond);
	if (virtual) {
		const id = getStableNodeId(cond, getNodeId);
		const event = virtual.component.type === 'button';
		const type = event ? 'virtualComponentEvent' : 'virtualComponentCondition';
		return {
			nodes: [
				createFlowNode(id, type, {
					label: event ? 'Virtual Button Event' : 'Virtual Value Condition',
					icon: event ? 'fa-circle-dot' : 'fa-code-compare',
					paletteKey: type,
					sceneData: cond,
					component: virtual.component,
					...(event
						? { event: String(virtual.value || 'S'), operator: 'eq' }
						: { operator: virtual.operator, value: virtual.value }),
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Manual execution ---
	if (purpose === 'manual_execution' || cond._gui_type === 'manual_execution') {
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'manualCondition', {
					label: 'Manual Execution',
					icon: 'fa-hand-pointer',
					paletteKey:
						cond._flow_card === 'webhookTrigger' ? 'webhookTrigger' : 'manualCondition',
					sceneData: cond,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Scene/alarm enabled state ---
	const sceneStateMatch = String(cond.r || '').match(/^blprop:([^:]+):enabled$/);
	if (
		sceneStateMatch &&
		(cond._flow_card === 'sceneCondition' ||
			cond._flow_card === 'alarmCondition' ||
			cond._gui_purpose === 'condition')
	) {
		const id = getStableNodeId(cond, getNodeId);
		const type = cond._flow_card === 'alarmCondition' ? 'alarmCondition' : 'sceneCondition';
		return {
			nodes: [
				createFlowNode(id, type, {
					label: type === 'alarmCondition' ? 'Alarm State' : 'Scene State',
					icon: type === 'alarmCondition' ? 'fa-bell' : 'fa-diagram-project',
					paletteKey: type,
					sceneData: cond,
					sceneId: sceneStateMatch[1],
					filterType: type === 'alarmCondition' ? 'alarm' : 'scene',
					state: String(cond.cmpval) === '0' ? 'disabled' : 'enabled',
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Time-based conditions ---
	if (cond._gui_type === 'timer') {
		const parsed = timerConditionInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'timeCondition', {
					label: `Every ${parsed.interval || parsed.dminslimit || '?'} min`,
					icon: 'fa-stopwatch',
					paletteKey: 'timeCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					timeSubType: 'timer',
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	if (cond._gui_type === 'd_cond') {
		const parsed = dailyConditionInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'timeCondition', {
					label: `Daily at ${parsed.time || '?'}`,
					icon: 'fa-calendar-day',
					paletteKey: 'timeCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					timeSubType: 'd_cond',
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	if (cond._gui_type === 'ot_cond') {
		const parsed = oneTimeConditionInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'timeCondition', {
					label: `Once at ${parsed.date || ''} ${parsed.time || ''}`.trim(),
					icon: 'fa-calendar-check',
					paletteKey: 'executeOnce',
					cardVersion: '1.0.0',
					sceneData: cond,
					timeSubType: 'ot_cond',
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Scene-as-trigger ---
	if (is_scene_as_trigger(cond)) {
		const parsed = automationRuns(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'sceneCondition', {
					label: `Scene runs: ${parsed.scene_id || 'unknown'}`,
					icon: 'fa-play-circle',
					sceneData: cond,
					sceneId: String(parsed?.scene_id ?? ''),
					state: 'enabled',
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Sunrise/Sunset ---
	if (is_sunrise_sunset_trigger(cond)) {
		const parsed = sunriseSunsetInterpretator(cond);
		const typeLabel = parsed.type === 'sunrise' ? 'Sunrise' : 'Sunset';
		const offsetLabel = parsed.offset_minutes
			? ` ${parsed.before_after === 'before' ? '-' : '+'}${parsed.offset_minutes}min`
			: '';
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'sunriseSunsetCondition', {
					label: `${typeLabel}${offsetLabel}`,
					icon: parsed.type === 'sunrise' ? 'fa-sun' : 'fa-moon',
					paletteKey: 'sunriseSunsetCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					...parsed,
					sunType: parsed.type,
					minutes: parsed.mins ?? 0,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Weather conditions ---
	if (is_weather_on_any_change_trigger(cond)) {
		const parsed = weatherOnAnyChangeInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'weatherCondition', {
					label: `Weather: ${parsed.cond || 'condition'}`,
					icon: 'fa-cloud-sun',
					paletteKey: 'weatherCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					...parsed,
					weatherCond: parsed.cond,
					operator: parsed.op,
					hourOffset: Number(parsed.hours ?? 0),
					executionType: 'any',
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	if (is_weather_one_time_trigger(cond)) {
		const parsed = weatherOneTimeInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'weatherCondition', {
					label: `Weather: ${parsed.cond || 'condition'}`,
					icon: 'fa-cloud-sun',
					paletteKey: 'weatherCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					...parsed,
					weatherCond: parsed.cond,
					operator: parsed.op,
					hourOffset: Number(parsed.hours ?? 0),
					executionType: 'once',
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Energy exchange ---
	if (is_energy_exchange_trigger(cond)) {
		const parsed = energyExchangeInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'energyCondition', {
					label: `Energy: ${parsed.exchange_market || 'market'}`,
					icon: 'fa-bolt',
					paletteKey: 'energyCondition',
					cardVersion: '1.0.0',
					sceneData: cond,
					...parsed,
					exchangeMarket: parsed.exchange_market,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Device triggers (use `rr`) -> unified ConditionNode with execution mode ---
	if (
		is_device_once_trigger(cond) ||
		is_device_on_any_change_trigger(cond) ||
		is_repeat_device_trigger(cond)
	) {
		return asPresence(createUnifiedDeviceConditionNode(cond, getNodeId, devicesMap));
	}

	// --- Expression-format device conditions: { eq: [{ rr: "dev:…" }, value] } ---
	if (is_device_expr_cond_with_ref(cond)) {
		const parsed = deviceConditionInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		const deviceName = getDeviceNameFromMap(parsed?.device?.id, devicesMap);
		const deviceCond = buildDeviceConditionData(parsed);
		return asPresence({
			nodes: [
				createFlowNode(id, 'condition', {
					label: buildDeviceConditionLabel(parsed, devicesMap),
					icon: 'fa-question-circle',
					sceneData: cond,
					device_name: deviceName || undefined,
					_device_condition: deviceCond._device_condition,
					sceneCapability: deviceCond.sceneCapability,
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		});
	}

	// --- Device conditions (use `r`) → single ConditionNode ---
	if (is_device_cond_op_as_key(cond)) {
		const parsed = deviceConditionInterpretator(cond);
		const id = getStableNodeId(cond, getNodeId);
		const deviceName = getDeviceNameFromMap(parsed?.device?.id, devicesMap);
		const deviceCond = buildDeviceConditionData(parsed);
		return asPresence({
			nodes: [
				createFlowNode(id, 'condition', {
					label: buildDeviceConditionLabel(parsed, devicesMap),
					icon: 'fa-question-circle',
					sceneData: cond,
					device_name: deviceName || undefined,
					_device_condition: deviceCond._device_condition,
					sceneCapability: deviceCond.sceneCapability,
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		});
	}

	if (is_device_cond_op_in_r(cond)) {
		const parsed = deviceConditionSecondFormat(cond);
		const id = getStableNodeId(cond, getNodeId);
		const deviceName = getDeviceNameFromMap(parsed?.device?.id, devicesMap);
		const deviceCond = buildDeviceConditionData(parsed);
		return asPresence({
			nodes: [
				createFlowNode(id, 'condition', {
					label: buildDeviceConditionLabel(parsed, devicesMap),
					icon: 'fa-question-circle',
					sceneData: cond,
					device_name: deviceName || undefined,
					_device_condition: deviceCond._device_condition,
					sceneCapability: deviceCond.sceneCapability,
					...parsed,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		});
	}

	// --- Variable condition: { r: "var:name", cmpop, cmpval } ---
	if (typeof cond.r === 'string' && cond.r.startsWith('var:')) {
		const varName = cond.r.slice(4);
		const id = getStableNodeId(cond, getNodeId);
		return {
			nodes: [
				createFlowNode(id, 'variableCondition', {
					label: `${varName} ${cond.cmpop ?? '?'} ${cond.cmpval ?? ''}`.trim(),
					icon: 'fa-code',
					color: '#f97316',
					sceneData: cond,
					variableName: varName,
					operator: cond.cmpop ?? 'eq',
					variableValue: cond.cmpval ?? '',
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Math condition ---
	if (cond._gui_type === 'math_based') {
		const id = getStableNodeId(cond, getNodeId);
		const parsed = mathConditionInterpretator(cond as any);
		return {
			nodes: [
				createFlowNode(id, 'mathCondition', {
					label: parsed.expression ? `When ${parsed.expression}` : 'Math Expression',
					icon: 'fa-square-root-variable',
					color: '#8b5cf6',
					sceneData: cond,
					expression: parsed.expression,
					params: parsed.params,
					mathScript: parsed.math_script,
					isValid: true,
					isConfigured: true,
				}),
			],
			edges: [],
			outputNodeId: id,
		};
	}

	// --- Fallback: unknown condition ---
	console.warn('SceneToFlow: Unknown condition format:', cond);
	const id = getStableNodeId(cond, getNodeId);
	return {
		nodes: [
			createFlowNode(id, 'condition', {
				label: 'Unknown Condition',
				icon: 'fa-question',
				sceneData: cond,
				isValid: false,
				isConfigured: false,
				requiresConfiguration: true,
			}),
		],
		edges: [],
		outputNodeId: id,
	};
}

/**
 * Convert a device trigger scene clause into a unified condition node.
 * This keeps trigger semantics (`once`/`any`/`repeat`, `hold_off`) directly
 * on the condition node so the editor has one node model for "when".
 */
function createUnifiedDeviceConditionNode(
	cond: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): NodeGroup {
	const parsed = deviceConditionInterpretator(cond);

	let executionMode: 'once' | 'any' | 'repeat' = 'once';
	if (is_device_once_trigger(cond)) executionMode = 'once';
	else if (is_device_on_any_change_trigger(cond)) executionMode = 'any';
	else if (is_repeat_device_trigger(cond)) executionMode = 'repeat';

	const conditionId = getStableNodeId(cond, getNodeId);
	const deviceName = getDeviceNameFromMap(parsed?.device?.id, devicesMap);
	const deviceCond = buildDeviceConditionData(parsed);
	const conditionNode = createFlowNode(conditionId, 'condition', {
		label: isPresenceSceneCondition(cond)
			? 'Presence'
			: buildDeviceConditionLabel(parsed, devicesMap),
		icon: 'fa-question-circle',
		device_name: deviceName || undefined,
		_device_condition: deviceCond._device_condition,
		sceneCapability: deviceCond.sceneCapability,
		// Device/property details + trigger execution details on one node
		...parsed,
		executionMode,
		hold_off: cond.hold_off,
		sceneData: cond,
		isValid: true,
		isConfigured: true,
	});

	return {
		nodes: [conditionNode],
		edges: [],
		outputNodeId: conditionId,
	};
}

// ---- Action processing ----

/**
 * Process action array into action nodes.
 */
function processActions(actions: any[], getNodeId: () => string, branch: 'do' | 'else'): FlowNode[] {
	const nodes: FlowNode[] = [];

	actions.forEach((action) => {
		if (action.wait !== undefined && action.do?.blk) {
			// Delay-wrapped action: store delay on the inner action node(s)
			const delayData = {
				value: action.wait,
				units: action.units === 'minutes' ? 'min' : 'sec',
			};
			const wid = action.wid;

			if (Array.isArray(action.do.blk)) {
				action.do.blk.forEach((innerAction: any) => {
					const node = parseActionToNode(innerAction, getNodeId, branch);
					if (node) {
						// Embed the delay data into the action node
						node.data.delay = delayData;
						node.data._wid = wid;
						nodes.push(node);
					}
				});
			}
		} else {
			const node = parseActionToNode(action, getNodeId, branch);
			if (node) nodes.push(node);
		}
	});

	return nodes;
}

function normalizeBranchStatements(content: any): any[] {
	if (content === null || content === undefined) return [];
	if (Array.isArray(content)) return content;
	if (typeof content === 'object' && Array.isArray(content.blk)) return content.blk;
	return [content];
}

function isIfStatement(statement: any): boolean {
	return !!statement && typeof statement === 'object' && statement.if && statement.do !== undefined;
}

function isActionSequenceStatement(statement: any): boolean {
	return Boolean(
		statement &&
		typeof statement === 'object' &&
		statement._actionSequenceNodeId &&
		Array.isArray(statement.blk),
	);
}

function parseActionSequenceSteps(statements: any[]): Array<Record<string, any>> {
	const parsed: Array<Record<string, any>> = [];
	const walk = (items: any[]) => {
		for (const statement of items) {
			const id = String(statement.wid || statement._fid || `step-${parsed.length + 1}`);
			if (typeof statement.wait === 'number') {
				parsed.push({
					id,
					type: 'wait',
					value: statement.wait,
					units: statement.units === 'minutes' ? 'min' : 'sec',
				});
				walk(normalizeBranchStatements(statement.do));
				continue;
			}
			if (statement._gui_type === 'set_variable' || statement.setvar) {
				parsed.push({
					id,
					type: 'setVariable',
					name: statement.name || statement.setvar || '',
					value: statement.value ?? '',
				});
				continue;
			}
			parsed.push({
				id,
				type: 'notification',
				notificationType: statement.notify || 'event_log',
				message: statement.msg || '',
			});
		}
	};
	walk(statements);
	return parsed;
}

/** Shelly scene delay block: `{ wait, units?, wid?, do?, _fid? }` (see FlowToScene timer serialization). */
function isWaitDoStatement(statement: any): boolean {
	return (
		!!statement &&
		typeof statement === 'object' &&
		typeof statement.wait === 'number' &&
		statement.do != null &&
		typeof statement.do === 'object'
	);
}

function normalizeWaitInnerDo(rawNested: any): any[] {
	if (Array.isArray(rawNested)) return rawNested;
	if (Array.isArray(rawNested.blk)) return rawNested.blk;
	return [rawNested];
}

/** Rebuild timer node + chained inner actions from a scene `wait` / `do` block. */
function materializeWaitDoChain(
	statement: any,
	getNodeId: () => string,
	branch: 'do' | 'else',
): { nodes: FlowNode[]; edges: FlowEdge[]; entryId: string; tailIds: string[] } {
	const timerId = getStableNodeId(statement, getNodeId);
	const units: 'min' | 'sec' = statement.units === 'minutes' ? 'min' : 'sec';
	const timerNode = createFlowNode(timerId, 'timer', {
		label: 'Timer',
		icon: 'fa-hourglass-half',
		timerMode: 'delay',
		delay: { value: statement.wait, units },
		_wid: statement.wid,
		paletteKey: statement._flow_card === 'debounceTimer' ? 'debounceTimer' : 'timer',
		branch,
		isValid: true,
		isConfigured: true,
	});

	const innerList = normalizeWaitInnerDo(statement.do);
	const nodes: FlowNode[] = [timerNode];
	const edges: FlowEdge[] = [];
	let prevId = timerId;
	let lastId = timerId;

	for (const inner of innerList) {
		const n = parseActionToNode(inner, getNodeId, branch);
		if (!n) continue;
		nodes.push(n);
		edges.push({
			id: `e-${prevId}-${n.id}`,
			source: prevId,
			target: n.id,
			sourceHandle: 'output',
			targetHandle: 'input',
			type: 'default',
			animated: true,
		});
		prevId = n.id;
		lastId = n.id;
	}

	return { nodes, edges, entryId: timerId, tailIds: [lastId] };
}

function buildBranchGraph(
	content: any,
	getNodeId: () => string,
	branch: 'do' | 'else',
	devicesMap: Record<string, { name?: string }> = {},
): BranchGraph {
	const statements = normalizeBranchStatements(content);
	const nodes: FlowNode[] = [];
	const edges: FlowEdge[] = [];
	let entryNodeId: string | null = null;
	let tailNodeIds: string[] = [];
	const gateTargetIds: string[] = [];

	let i = 0;
	while (i < statements.length) {
		const statement = statements[i];

		if (isActionSequenceStatement(statement)) {
			const sequenceId = String(statement._actionSequenceNodeId);
			const sequenceNode = createFlowNode(sequenceId, 'actionSequence', {
				label: 'Action Sequence',
				icon: 'fa-list-ol',
				steps: parseActionSequenceSteps(statement.blk),
				branch,
				isValid: statement.blk.length > 0,
				isConfigured: statement.blk.length > 0,
			});
			nodes.push(sequenceNode);
			if (!entryNodeId) entryNodeId = sequenceId;
			if (tailNodeIds.length === 0) gateTargetIds.push(sequenceId);
			else
				for (const tailId of tailNodeIds)
					edges.push({
						id: `e-${tailId}-${sequenceId}`,
						source: tailId,
						target: sequenceId,
						sourceHandle: 'output',
						targetHandle: 'input',
						type: 'default',
						animated: true,
					});
			tailNodeIds = [sequenceId];
			i++;
			continue;
		}

		if (isIfStatement(statement)) {
			const nested = buildIfStatementGraph(statement, getNodeId, devicesMap);
			nodes.push(...nested.nodes);
			edges.push(...nested.edges);
			if (!nested.entryNodeId) {
				i++;
				continue;
			}

			if (!entryNodeId) entryNodeId = nested.entryNodeId;

			if (tailNodeIds.length === 0) {
				gateTargetIds.push(nested.entryNodeId);
			} else {
				for (const tailId of tailNodeIds) {
					edges.push({
						id: `e-${tailId}-${nested.entryNodeId}`,
						source: tailId,
						target: nested.entryNodeId,
						sourceHandle: 'output',
						targetHandle: 'input',
						type: 'default',
						animated: true,
					});
				}
			}
			tailNodeIds = nested.tailNodeIds;
			i++;
			continue;
		}

		if (isWaitDoStatement(statement)) {
			const {
				nodes: wNodes,
				edges: wEdges,
				entryId,
				tailIds,
			} = materializeWaitDoChain(statement, getNodeId, branch);
			nodes.push(...wNodes);
			edges.push(...wEdges);
			i++;

			if (!entryNodeId) entryNodeId = entryId;

			if (tailNodeIds.length === 0) {
				gateTargetIds.push(entryId);
				tailNodeIds = tailIds;
			} else {
				for (const tailId of tailNodeIds) {
					edges.push({
						id: `e-${tailId}-${entryId}`,
						source: tailId,
						target: entryId,
						sourceHandle: 'output',
						targetHandle: 'input',
						type: 'default',
						animated: true,
					});
				}
				tailNodeIds = tailIds;
			}
			continue;
		}

		const batchNodes: FlowNode[] = [];
		while (i < statements.length && !isIfStatement(statements[i])) {
			const node = parseActionToNode(statements[i], getNodeId, branch);
			i++;
			if (node) batchNodes.push(node);
		}

		if (batchNodes.length === 0) continue;

		for (const node of batchNodes) {
			nodes.push(node);
			if (!entryNodeId) entryNodeId = node.id;
		}

		if (tailNodeIds.length === 0) {
			// Leading actions under a branch: same as multiple true/false handles from the parent — parallel
			for (const node of batchNodes) {
				gateTargetIds.push(node.id);
			}
			tailNodeIds = batchNodes.map((n) => n.id);
		} else {
			// After nested if / prior segment: merge tails → first action, then chain within the batch
			const first = batchNodes[0];
			for (const tailId of tailNodeIds) {
				edges.push({
					id: `e-${tailId}-${first.id}`,
					source: tailId,
					target: first.id,
					sourceHandle: 'output',
					targetHandle: 'input',
					type: 'default',
					animated: true,
				});
			}
			for (let k = 1; k < batchNodes.length; k++) {
				const prev = batchNodes[k - 1];
				const cur = batchNodes[k];
				edges.push({
					id: `e-${prev.id}-${cur.id}`,
					source: prev.id,
					target: cur.id,
					sourceHandle: 'output',
					targetHandle: 'input',
					type: 'default',
					animated: true,
				});
			}
			tailNodeIds = [batchNodes[batchNodes.length - 1].id];
		}
	}

	return { nodes, edges, entryNodeId, tailNodeIds, gateTargetIds };
}

function mergeBranchGraphInto(nodes: FlowNode[], edges: FlowEdge[], graph: BranchGraph): void {
	const nodeIds = new Set(nodes.map((n) => String(n.id)));
	for (const node of graph.nodes) {
		if (!nodeIds.has(String(node.id))) {
			nodeIds.add(String(node.id));
			nodes.push(node);
		}
	}

	const edgeTopo = new Set(
		edges.map((e) => `${e.source}\0${e.sourceHandle ?? ''}\0${e.target}\0${e.targetHandle ?? ''}`),
	);
	for (const edge of graph.edges) {
		const key = `${edge.source}\0${edge.sourceHandle ?? ''}\0${edge.target}\0${edge.targetHandle ?? ''}`;
		if (!edgeTopo.has(key)) {
			edgeTopo.add(key);
			edges.push(edge);
		}
	}
}

function buildIfStatementGraph(
	statement: any,
	getNodeId: () => string,
	devicesMap: Record<string, { name?: string }> = {},
): BranchGraph {
	if (statement?._switchNodeId) {
		const graph = buildSwitchStatementGraph(statement, getNodeId, devicesMap);
		const switchId = String(statement._switchNodeId);
		return {
			...graph,
			entryNodeId: switchId,
			tailNodeIds: graph.nodes
				.filter((node) => node.type !== 'switchNode')
				.map((node) => String(node.id)),
			gateTargetIds: [switchId],
		};
	}
	if (statement?._hysteresisNodeId) {
		const graph = buildHysteresisStatementGraph(statement, getNodeId, devicesMap);
		const id = String(statement._hysteresisNodeId);
		return {
			...graph,
			entryNodeId: id,
			tailNodeIds: graph.nodes.filter((node) => node.id !== id).map((node) => node.id),
			gateTargetIds: [id],
		};
	}
	const nodes: FlowNode[] = [];
	const edges: FlowEdge[] = [];
	const nestedScene = { if: statement.if } as ISceneObj;
	const conditionGraph = processConditions(nestedScene, getNodeId, devicesMap);
	nodes.push(...conditionGraph.conditionNodes);
	edges.push(...conditionGraph.conditionEdges);

	const decisionOutputId = conditionGraph.gateNodeId || conditionGraph.lastOutputId;
	const entryNodeId = findSubgraphEntryNodeId(
		conditionGraph.conditionNodes,
		conditionGraph.conditionEdges,
	);

	const doBranch = buildBranchGraph(statement.do, getNodeId, 'do', devicesMap);
	const elseBranch = buildBranchGraph(statement.else, getNodeId, 'else', devicesMap);
	nodes.push(...doBranch.nodes, ...elseBranch.nodes);
	edges.push(...doBranch.edges, ...elseBranch.edges);

	if (decisionOutputId) {
		for (const targetId of doBranch.gateTargetIds) {
			edges.push({
				id: `e-${decisionOutputId}-${targetId}-true`,
				source: decisionOutputId,
				target: targetId,
				sourceHandle: 'true',
				targetHandle: 'input',
				type: 'default',
				animated: true,
			});
		}
		for (const targetId of elseBranch.gateTargetIds) {
			edges.push({
				id: `e-${decisionOutputId}-${targetId}-false`,
				source: decisionOutputId,
				target: targetId,
				sourceHandle: 'false',
				targetHandle: 'input',
				type: 'default',
				animated: true,
			});
		}
	}

	const tailNodeIds = [...doBranch.tailNodeIds, ...elseBranch.tailNodeIds];
	return { nodes, edges, entryNodeId, tailNodeIds, gateTargetIds: entryNodeId ? [entryNodeId] : [] };
}

function findSubgraphEntryNodeId(nodes: FlowNode[], edges: FlowEdge[]): string | null {
	if (nodes.length === 0) return null;
	const ids = new Set(nodes.map((n) => n.id));
	const withIncoming = new Set(
		edges.filter((e) => ids.has(e.source) && ids.has(e.target)).map((e) => e.target),
	);
	const root = nodes.find((n) => !withIncoming.has(n.id));
	return root?.id || nodes[0].id;
}

/**
 * Parse a single action JSON into a FlowNode.
 */
function parseActionToNode(
	action: any,
	getNodeId: () => string,
	branch: 'do' | 'else',
): FlowNode | null {
	if (!action) return null;

	const id = getStableNodeId(action, getNodeId);
	const purpose = action._gui_type || action._gui_function;

	// Notification
	if (purpose === 'notification' || action.notify) {
		const msgType = action.msg_type || 'push';
		const parsed = notificationActionInterpretator(action, msgType);
		const resolvedType = parsed?.type ?? action.notify ?? msgType;
		const pushMsgType = msgType === 'alarm' ? 'alarm' : 'push';
		return createFlowNode(id, 'notificationAction', {
			label: getNotificationTypeLabel(resolvedType),
			icon: 'fa-bell',
			sceneData: action,
			branch,
			notificationType: resolvedType,
			notificationMessage: parsed?.msg ?? action.msg ?? '',
			...parsed,
			paletteKey: resolveNotificationPaletteKey(resolvedType, pushMsgType),
			...(resolvedType === 'push_notification' ? { pushMsgType } : {}),
			isValid: true,
			isConfigured: true,
		});
	}

	// Virtual component value write / emitted button event.
	const virtualActionRef = parseVirtualComponentRef(action.r);
	if (virtualActionRef && action.set !== undefined) {
		const parentDeviceId = String(
			action?._flow_virtual_component?.parentDeviceId ||
				action?._gui_device_id ||
				virtualActionRef.backendId,
		).split('_')[0];
		const componentKey = `${virtualActionRef.type}:${virtualActionRef.index}`;
		const component = {
			...(action?._flow_virtual_component || {}),
			ref: `${parentDeviceId}|${componentKey}`,
			parentDeviceId,
			componentKey,
			type: virtualActionRef.type,
			index: virtualActionRef.index,
			name:
				action?._flow_virtual_component?.name ||
				`${virtualActionRef.type} ${virtualActionRef.index}`,
		};
		const emitButton =
			virtualActionRef.type === 'button' || action._flow_card === 'emitVirtualButtonEvent';
		return createFlowNode(id, 'virtualComponentAction', {
			label: emitButton ? 'Emit Virtual Button Event' : 'Set Virtual Component',
			icon: emitButton ? 'fa-hand-pointer' : 'fa-sliders',
			paletteKey: emitButton ? 'emitVirtualButtonEvent' : 'virtualComponentAction',
			sceneData: action,
			branch,
			component,
			...(emitButton ? { event: String(action.set || 'S') } : { value: action.set }),
			isValid: true,
			isConfigured: true,
		});
	}

	// Scene activation
	if (purpose === 'activate_scene' || (typeof action.trigger === 'string' && !action.r)) {
		const parsed = sceneActionInterpretator(action);
		const playOption =
			parsed?.option === 'without_if_evaluation' ? 'without_if_evaluation' : 'with_if_evaluation';
		const type = action._flow_card === 'alarmAction' ? 'alarmAction' : 'sceneAction';
		return createFlowNode(id, type, {
			label: `Run Scene: ${parsed?.scene_id || 'unknown'}`,
			icon: 'fa-play-circle',
			sceneData: action,
			branch,
			paletteKey: type,
			filterType: type === 'alarmAction' ? 'alarm' : 'scene',
			sceneId: String(parsed?.scene_id ?? ''),
			actionType: 'play',
			playOption,
			...parsed,
			isValid: true,
			isConfigured: true,
		});
	}

	// Enable/disable scene
	if (purpose === 'enable_disable_scene' || (action.r && String(action.r).startsWith('blprop:'))) {
		const parsed = enableDisableSceneActionInterpretator(action);
		const actionLabel = parsed?.option?.includes('enable')
			? 'Enable'
			: parsed?.option?.includes('disable')
				? 'Disable'
				: 'Toggle';
		const actionType = parsed?.option?.includes('enable')
			? 'enable'
			: parsed?.option?.includes('disable')
				? 'disable'
				: 'toggle';
		const type = action._flow_card === 'alarmAction' ? 'alarmAction' : 'sceneAction';
		return createFlowNode(id, type, {
			label: `${actionLabel} Scene: ${parsed?.scene_id || 'unknown'}`,
			icon: 'fa-toggle-on',
			sceneData: action,
			branch,
			paletteKey: type,
			filterType: type === 'alarmAction' ? 'alarm' : 'scene',
			sceneId: String(parsed?.scene_id ?? ''),
			actionType,
			...parsed,
			isValid: true,
			isConfigured: true,
		});
	}

	// Group action
	if (purpose === 'group_action' || action.grpctrl !== undefined) {
		const parsed = groupActionInterpretator(action);
		const isRollerAction = action.position !== undefined || action.direction !== undefined;
		const isThermostatAction = action.enable !== undefined || action.target_t !== undefined;
		const isLightBrightnessAction =
			action.brightness !== undefined && !isRollerAction && !isThermostatAction;

		let groupType: string;
		let resolvedAction: string;
		let thermostatTemperature: number | undefined;
		let brightness: number | undefined;

		if (isRollerAction) {
			groupType = 'roller';
			const firstAction = Array.isArray(parsed?.actions) ? parsed.actions[0] : null;
			resolvedAction = String(firstAction?.value ?? action.direction ?? action.position ?? 'open');
		} else if (isThermostatAction) {
			groupType = 'thermostat';
			if (action.enable === true) resolvedAction = 'enable';
			else if (action.enable === false) resolvedAction = 'disable';
			else {
				resolvedAction = 'set_temperature';
				thermostatTemperature = Number(action.target_t);
			}
		} else if (isLightBrightnessAction) {
			groupType = 'light';
			resolvedAction = 'set_brightness';
			brightness = Number(action.brightness);
		} else {
			groupType = 'relay';
			const firstAction = Array.isArray(parsed?.actions) ? parsed.actions[0] : null;
			resolvedAction = String(firstAction?.value ?? action.turn ?? 'on');
		}

		return createFlowNode(id, 'groupAction', {
			label: `Group: ${parsed?.group_id ?? 'unknown'}`,
			icon: 'fa-layer-group',
			sceneData: action,
			branch,
			paletteKey: 'groupAction',
			groupId: String(parsed?.group_id ?? ''),
			action: resolvedAction,
			groupType,
			rollerPosition: Number(action.position ?? 100),
			rollerDirection: (action.direction as string) || 'open',
			thermostatTemperature,
			brightness,
			...parsed,
			isValid: true,
			isConfigured: true,
		});
	}

	// Set variable
	if (purpose === 'set_variable' || action.setvar) {
		const variableName = action.name || action.setvar || 'variable';
		return createFlowNode(id, 'setVariable', {
			label: `Set ${variableName}`,
			icon: 'fa-equals',
			sceneData: action,
			branch,
			variable: {
				name: variableName,
				value: action.value ?? '',
			},
			isValid: true,
			isConfigured: true,
		});
	}

	// Run script (must precede generic dev: device actions)
	const scriptData = parseRunScriptFromSceneAction(action);
	if (scriptData) {
		const scriptCommandLabels: Record<string, string> = {
			start: 'Run',
			stop: 'Stop',
			enable: 'Enable',
			disable: 'Disable',
		};
		const commandLabel = scriptCommandLabels[scriptData.command] ?? 'Run';
		return createFlowNode(id, 'scriptAction', {
			label: `${commandLabel} script ${scriptData.scriptId}`,
			icon: 'fa-brackets-curly',
			paletteKey: 'scriptAction',
			cardVersion: '1.0.0',
			sceneData: action,
			branch,
			...scriptData,
			isValid: true,
			isConfigured: true,
		});
	}

	// Device action (default)
	if (action.r && String(action.r).startsWith('dev:')) {
		const parsed = deviceActionInterpretator(action);
		return createFlowNode(id, 'action', {
			label: buildActionLabel(parsed),
			icon: 'fa-microchip',
			sceneData: action,
			branch,
			...parsed,
			isValid: true,
			isConfigured: true,
		});
	}

	// Fallback device action
	if (action.r || action.set !== undefined) {
		const parsed = deviceActionInterpretator(action);
		return createFlowNode(id, 'action', {
			label: buildActionLabel(parsed),
			icon: 'fa-microchip',
			sceneData: action,
			branch,
			...parsed,
			isValid: true,
			isConfigured: true,
		});
	}

	// Unknown action
	console.warn('SceneToFlow: Unknown action format:', action);
	return createFlowNode(id, 'action', {
		label: 'Unknown Action',
		icon: 'fa-question',
		sceneData: action,
		branch,
		isValid: false,
		isConfigured: false,
	});
}

// ---- Helper functions ----

function createFlowNode(id: string, type: string, data: Record<string, any>): FlowNode {
	return {
		id,
		type: type as NodeType,
		position: { x: 0, y: 0 },
		data: {
			...data,
			color: data.color || NODE_COLORS[type as keyof typeof NODE_COLORS] || '#666',
		},
		validated: data.isValid !== false,
		errors: data.validationErrors || [],
	};
}

/** Look up a device name from a pre-loaded map (avoids repeated store reads). */
function getDeviceNameFromMap(
	deviceId: string | null | undefined,
	devicesMap: Record<string, { name?: string }>,
): string {
	if (!deviceId) return '';
	return devicesMap?.[deviceId]?.name || '';
}

/** Legacy fallback — reads the store directly. Use only when no pre-loaded map is available. */
function getDeviceNameFromStore(deviceId: string | null | undefined): string {
	if (!deviceId) return '';
	try {
		const map = get(devices) as Record<string, { name?: string }>;
		return map?.[deviceId]?.name || '';
	} catch {
		return '';
	}
}

/** Build _device_condition and sceneCapability for ConditionInspector compatibility. */
function buildDeviceConditionData(parsed: any): {
	_device_condition?: any;
	sceneCapability?: any;
} {
	if (!parsed?.device?.id) return {};
	const device = parsed.device;
	const deviceId = device.id;
	const channel = String(device.channel ?? '0');
	const condName = parsed.cond_name || parsed.property || '';
	const operator = parsed.operator ?? parsed.cmpop ?? '';
	const value = parsed.value ?? parsed.cmpval ?? '';
	const cmpop = toSceneOperator(String(operator));

	return {
		_device_condition: {
			device: { id: deviceId, channel },
			cond_name: condName,
			operator,
			value,
			cmpop,
			cmpval: value,
		},
		sceneCapability: {
			deviceId,
			channel,
			operator,
			capabilityName: condName,
			value,
		},
	};
}

function buildDeviceConditionLabel(parsed: any, devicesMap?: Record<string, { name?: string }>): string {
	const deviceId = parsed?.device?.id;
	const deviceName =
		(deviceId
			? devicesMap
				? getDeviceNameFromMap(deviceId, devicesMap)
				: getDeviceNameFromStore(deviceId)
			: '') ||
		parsed?.device_name ||
		parsed?.device_id ||
		'Device';
	return conditionToVerboseLabel({
		deviceId,
		deviceName,
		channel: parsed?.device?.channel,
		propertyName: parsed?.cond_name || parsed?.property || '',
		operator: parsed?.operator ?? parsed?.cmpop,
		value: parsed?.value ?? parsed?.cmpval,
	});
}

function buildActionLabel(parsed: any): string {
	if (!parsed) return 'Device Action';
	const deviceName = parsed.device_name || parsed.device_id || 'Device';
	const actions = parsed.actions;
	if (Array.isArray(actions) && actions.length > 0) {
		const firstAction = actions[0];
		return `${deviceName}: ${firstAction.name || ''} = ${firstAction.value ?? ''}`.trim();
	}
	return `${deviceName} action`;
}

function getNotificationTypeLabel(type: string): string {
	const labels: Record<string, string> = {
		push_notification: 'Push Notification',
		mail_notification: 'Email Notification',
		event_log: 'Event Log',
		alexa_notif: 'Alexa Notification',
		alexa_notif_premium: 'Alexa Announcement',
	};
	return labels[type] || 'Notification';
}
